/**
 * Whether the viewer owns the lesson sequencing page (P-PRD-525 R31).
 *
 * Sequencing belongs to the teacher who owns the classroom. The page is
 * refused -- reads as well as writes -- to anyone acting for another teacher,
 * co-teacher and org admin alike, and an org admin keeps it only for classes
 * she teaches herself. `get_sequencing_teacher` is the guard; this mirrors it
 * so the page can say why instead of collecting 403s.
 *
 * The same identity rule the server uses: `is_you` on `/accounts/accessible`
 * is `t.user_id == current_user.id`. `teacher_id > 0` because an org admin
 * with no teacher row of her own still gets a synthesised "You" entry, keyed
 * `-current_user.id`, and owns no class.
 */
import type { AccessibleAccount } from "@/hooks/useAccounts";

/**
 * "unknown" while the accounts that decide it are loading. "unresolved" when
 * that lookup failed (network, 5xx): it answered nothing, so the server --
 * which refuses a non-owner the page anyway -- has to decide.
 */
export type SequencingOwnership =
  "owner" | "not_owner" | "unknown" | "unresolved";

export const sequencingOwnership = ({
  actingTeacherId,
  isOrgAdmin,
  accounts,
  accountsLoading,
  accountsFailed = false,
}: {
  actingTeacherId: number | null;
  isOrgAdmin: boolean;
  accounts: AccessibleAccount[];
  accountsLoading: boolean;
  accountsFailed?: boolean;
}): SequencingOwnership => {
  // A teacher on her own account: "You" is a null header, and only her own
  // classes come back. Decided without waiting on the accounts request.
  if (actingTeacherId === null && !isOrgAdmin) return "owner";
  const account =
    actingTeacherId === null
      ? accounts.find((candidate) => candidate.is_you)
      : accounts.find((candidate) => candidate.teacher_id === actingTeacherId);
  if (account === undefined && accountsLoading) return "unknown";
  if (account === undefined && accountsFailed) return "unresolved";
  return account?.is_you && account.teacher_id > 0 ? "owner" : "not_owner";
};
